import React from "react";
import { ThemeProvider } from "styled-components";

import { FAVICON_CDN } from "@/config";
import { BrandSettings } from "@/service/Brands";
import IFrameService from "@/service/IFrame";
import InteractionService from "@/service/Interaction";
import {
  Interaction,
  InteractionLayout,
  InteractionResult,
} from "@/service/Model";
import ResizeService from "@/service/Resize";
import TrackingService from "@/service/Tracking";
import { GlobalStyles, Theme } from "@/styles";
import { IFontFamily } from "@/styles/GlobalStyles";

type AppProps = {
  brand: BrandSettings;
  interaction: Interaction;
  interactionResults?: InteractionResult;
  onDestroy: () => void;
};

type AppState = {
  fonts: keyof IFontFamily;
  theme: ITheme;
  interactionType: string;
  interactionLayout?: InteractionLayout;
};

export type ReactComponent<P = {}> =
  | React.ComponentClass<P>
  | React.FunctionComponent<P>;

const App = <P extends {}>(WrappedComponent: ReactComponent) =>
  class extends React.Component<P & AppProps, AppState> {
    public constructor(props: P & AppProps) {
      super(props);
      const fonts = props.brand.theme as keyof IFontFamily;
      const theme = (Theme as any)[fonts] as ITheme;

      if (this.useDynamicHeight(props.interaction)) {
        // Overwrite theme setting - content will stretch the height and la-hyper will resize the iframe
        theme.globals.minInteractionHeight = "0";
      }

      this.state = {
        fonts: fonts,
        theme,
        interactionType: props.interaction.type,
        interactionLayout: props.interaction.layout,
      };
    }

    public componentDidMount(): void {
      const { interaction } = this.props;
      if (!interaction) {
        return;
      }
      // Track interaction initialization
      TrackingService.getInstance().interaction = interaction;

      // IFrameService initialization
      IFrameService.initialized();

      // Extract tealium via LA-Hyper
      IFrameService.getTealiumConfig();

      // Get canonical url
      IFrameService.getCanonical();

      ResizeService.init(this.onResize, 200);

      // Handles theme based favicon
      this.applyFavicon();

      // Set interaction title as document title
      document.title = interaction.title;
    }

    public render(): JSX.Element {
      return (
        <ThemeProvider theme={this.state.theme}>
          <React.Fragment>
            <GlobalStyles fonts={this.state.fonts} />
            <WrappedComponent {...this.props} />
          </React.Fragment>
        </ThemeProvider>
      );
    }

    /**
     * @func applyFavicon
     * Handles favicon based on theme
     */
    public applyFavicon(): void {
      const { interactionType, interactionLayout, fonts } = this.state;
      const link: HTMLLinkElement =
        document.querySelector("link[rel*='icon']") ||
        document.createElement("link");
      link.type = "image/x-icon";
      link.rel = "shortcut icon";
      if (interactionLayout) {
        link.href = `${FAVICON_CDN}/${interactionType}/${interactionLayout}/${fonts}/favicon.ico`;
      } else {
        link.href = `${FAVICON_CDN}/${interactionType}/${fonts}/favicon.ico`;
      }

      document.getElementsByTagName("head")[0].appendChild(link);
    }

    public onResize(height: number): void {
      IFrameService.resize(height);
    }

    public useDynamicHeight(interaction: Interaction): boolean {
      return !InteractionService.hasBackgroundImage(interaction);
    }
  };

export default App;
